import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Upload, Download, Calendar as CalendarIcon, FileSpreadsheet, AlertCircle, CheckCircle2, Trash2, FileDown, Grid3x3, ClipboardList, Search, RefreshCw, FileCode, Users, Clock, BookOpen, BarChart3, ChevronLeft, ChevronRight, X, Sparkles } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { getSubjectColor } from '@/components/timetables/subjectColor';
import { CalendarGridView } from '@/components/timetables/CalendarGridView';
import { TeacherGridView } from '@/components/timetables/TeacherGridView';
import { ClassGridView } from '@/components/timetables/ClassGridView';
import { DepartmentGridView } from '@/components/timetables/DepartmentGridView';
import { HeatmapView } from '@/components/timetables/HeatmapView';

const TimetablesPage = () => {
  const { token, user } = useContext(AuthContext);
  const [timetables, setTimetables] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [seedingDemo, setSeedingDemo] = useState(false);
  const [ascDialogOpen, setAscDialogOpen] = useState(false);
  const [ascMode, setAscMode] = useState('xml'); // 'xml' or 'edupage'
  const [edupageCreds, setEdupageCreds] = useState({ domain: '', username: '', password: '' });
  const [syncing, setSyncing] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(user?.role !== 'admin' ? (user?.id || '') : '');
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [classLevels, setClassLevels] = useState([]);
  const [formData, setFormData] = useState({
    teacher_id: '',
    day: 'Monday',
    period: '',
    time_slot: '',
    class_name: '',
    subject: ''
  });

  // Import state
  const [importFile, setImportFile] = useState(null);
  const [clearExisting, setClearExisting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const xmlInputRef = useRef(null);

  // ASC XML upload handler
  const handleXmlUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSyncing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/timetables/import-xml`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const d = res.data;
      toast.success(`XML import: ${d.imported} entries imported, ${d.skipped} skipped`);
      if (d.not_matched_teachers?.length > 0) {
        toast.warning(`Unmatched teachers: ${d.not_matched_teachers.join(', ')}`);
      }
      setAscDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'XML import failed');
    } finally {
      setSyncing(false);
    }
  };

  // EduPage API sync handler
  const handleEdupageSync = async () => {
    if (!edupageCreds.domain || !edupageCreds.username || !edupageCreds.password) {
      toast.error('Please fill in all EduPage credentials');
      return;
    }
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/timetables/sync-edupage`, edupageCreds, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = res.data;
      toast.success(`EduPage sync: ${d.imported} entries imported from ${d.total_lessons} lessons`);
      setAscDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'EduPage sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const [periodsPerDay, setPeriodsPerDay] = useState(8);
  const periods = Array.from({ length: periodsPerDay }, (_, i) => `Period ${i + 1}`);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);

  // Delete All confirmation state
  const [deleteStep, setDeleteStep] = useState(0); // 0=closed, 1=first warning, 2=type confirmation
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Derived data: all unique classes and subjects
  const allClasses = useMemo(() => [...new Set(timetables.map(e => e.class_name))].sort(), [timetables]);
  const allSubjects = useMemo(() => [...new Set(timetables.map(e => e.subject))].sort(), [timetables]);

  // Universal quick-search results
  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q || q.length < 1) return null;
    const matchedTeachers = teachers.filter(t => t.name.toLowerCase().includes(q)).slice(0, 5);
    const matchedClasses = allClasses.filter(c => c.toLowerCase().includes(q)).slice(0, 5);
    const matchedSubjects = allSubjects.filter(s => s.toLowerCase().includes(q)).slice(0, 5);
    if (!matchedTeachers.length && !matchedClasses.length && !matchedSubjects.length) return null;
    return { teachers: matchedTeachers, classes: matchedClasses, subjects: matchedSubjects };
  }, [searchQuery, teachers, allClasses, allSubjects]);

  const handleSearchSelect = (type, value) => {
    if (type === 'teacher') {
      setSelectedTeacher(value);
      setViewMode('teacher-grid');
    } else if (type === 'class') {
      setSelectedClass(value);
      setViewMode('class-grid');
    } else if (type === 'subject') {
      setSelectedSubject(value);
      setViewMode('department');
    }
    setSearchQuery('');
    setSearchFocused(false);
  };

  // Common Free Periods Finder state
  const [cfpSelectedTeachers, setCfpSelectedTeachers] = useState([]);
  const [cfpDay, setCfpDay] = useState('Monday');
  const [cfpResult, setCfpResult] = useState(null);
  const [cfpLoading, setCfpLoading] = useState(false);
  const [cfpTeacherSearch, setCfpTeacherSearch] = useState('');

  const toggleCfpTeacher = (teacherId) => {
    setCfpSelectedTeachers(prev =>
      prev.includes(teacherId) ? prev.filter(id => id !== teacherId) : [...prev, teacherId]
    );
    setCfpResult(null);
  };

  const findCommonFreePeriods = async () => {
    if (cfpSelectedTeachers.length < 2) { toast.error('Select at least 2 teachers'); return; }
    setCfpLoading(true);
    try {
      const res = await axios.get(`${API}/timetables/common-free-periods?teacher_ids=${cfpSelectedTeachers.join(',')}&day=${cfpDay}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCfpResult(res.data);
    } catch { toast.error('Failed to find common free periods'); }
    finally { setCfpLoading(false); }
  };

  const filteredTimetables = useMemo(() => {
    let filtered = timetables;
    if (selectedTeacher?.trim()) {
      filtered = filtered.filter(t => t.teacher_id === selectedTeacher);
    }
    if (selectedDay?.trim()) {
      filtered = filtered.filter(t => t.day === selectedDay);
    }
    if (selectedPeriod?.trim()) {
      filtered = filtered.filter(t => t.period === selectedPeriod);
    }
    if (selectedLevel?.trim()) {
      const lvl = classLevels.find(l => l.id === selectedLevel);
      const cls = new Set((lvl?.classes || []));
      filtered = filtered.filter(t => cls.has(t.class_name));
    }
    return filtered;
  }, [timetables, selectedTeacher, selectedDay, selectedPeriod, selectedLevel, classLevels]);

  useEffect(() => {
    // Fetch class levels once so level filter works across views
    axios.get(`${API}/class-levels`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setClassLevels(res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Fetch periods_per_day from config
    axios.get(`${API}/config`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (res.data?.periods_per_day) setPeriodsPerDay(res.data.periods_per_day); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedTeacher, selectedDay]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedTeacher) params.append('teacher_id', selectedTeacher);
      if (selectedDay) params.append('day', selectedDay);

      // Admins use /api/users for full list; teachers use /api/users/teachers-list (auth-only, no admin needed)
      const isAdminUser = user?.role === 'admin';
      const teachersUrl = isAdminUser ? `${API}/users` : `${API}/users/teachers-list`;

      const [timetablesRes, teachersRes] = await Promise.all([
        axios.get(`${API}/timetables?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(teachersUrl, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTimetables(timetablesRes.data);
      setTeachers(
        teachersRes.data
          .filter(u => u.role !== 'admin' && u.is_active)
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'en', { sensitivity: 'base' }))
      );
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Failed to load timetables:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/timetables`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Timetable entry created');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create entry');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this timetable entry?')) return;
    try {
      await axios.delete(`${API}/timetables/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleDeleteAllConfirmed = async () => {
    try {
      const res = await axios.delete(`${API}/timetables`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setDeleteStep(0);
      setDeleteConfirmText('');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete timetables');
    }
  };

  const resetForm = () => {
    setFormData({
      teacher_id: '',
      day: 'Monday',
      period: '',
      time_slot: '',
      class_name: '',
      subject: ''
    });
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/timetables/template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'timetable_template.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!['csv', 'xlsx', 'xls'].includes(ext)) {
        toast.error('Please select a CSV or Excel (.xlsx) file');
        e.target.value = '';
        return;
      }
      setImportFile(file);
      setImportResult(null);
    }
  };

  const handleSeedDemo = async () => {
    const confirmed = window.confirm(
      'This will REPLACE all existing timetable entries with an MOE-aligned demo schedule for 12 classes across Sec 1–4 (Express, N(A), and N(T) streams).\n\n' +
      'Uses authentic MOE subject names (Pure Physics/Chemistry/Biology, Additional Mathematics, Social Studies, Mobile Robotics, Smart Electrical Technology, Retail Operations, etc.).\n\n' +
      'All currently seeded teachers will be assigned subjects based on their role. Assembly is excluded.\n\n' +
      'Continue?'
    );
    if (!confirmed) return;
    setSeedingDemo(true);
    try {
      const res = await axios.post(`${API}/timetables/seed-demo`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Demo timetable seeded — ${res.data.entries_created} entries across ${res.data.classes.length} classes & ${res.data.teachers_used} teachers.`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to seed demo timetable');
    } finally {
      setSeedingDemo(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Please select a file first');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const res = await axios.post(
        `${API}/timetables/import?clear_existing=${clearExisting}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setImportResult(res.data);
      if (res.data.created > 0) {
        toast.success(`Imported ${res.data.created} timetable entries`);
        fetchData();
      } else if (res.data.error_count > 0) {
        toast.error('Import completed with errors. Check the results below.');
      } else {
        toast.info('No new entries imported (all duplicates or empty file)');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Import failed');
      setImportResult({ message: detail || 'Import failed', created: 0, skipped: 0, error_count: 0, errors: [] });
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setImportResult(null);
    setClearExisting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const isAdmin = user?.role === 'admin';

  // Group timetables by day for better display
  const groupedByDay = days.reduce((acc, day) => {
    acc[day] = filteredTimetables.filter(t => t.day === day).sort((a, b) => {
      const pA = parseInt(a.period.replace('Period ', ''));
      const pB = parseInt(b.period.replace('Period ', ''));
      return pA - pB;
    });
    return acc;
  }, {});

  // State for view mode — teachers default to their own weekly grid
  const isTeacherUser = user?.role !== 'admin';
  const [viewMode, setViewMode] = useState(isTeacherUser ? 'teacher-grid' : 'list');

  // Class grid + Department + Pagination state
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [listPage, setListPage] = useState(1);
  const LIST_PAGE_SIZE = 20;

  const handleExportTimetable = async (format = 'excel') => {
    const viewParam = viewMode === 'teacher-grid' ? 'teacher' : viewMode === 'class-grid' ? 'class' : viewMode;
    const params = new URLSearchParams({ view: viewParam });
    if ((viewMode === 'teacher-grid' || viewMode === 'list') && selectedTeacher?.trim()) params.set('teacher_id', selectedTeacher);
    if (viewMode === 'class-grid' && selectedClass?.trim()) params.set('class_name', selectedClass);
    if (viewMode === 'department' && selectedSubject?.trim()) params.set('subject', selectedSubject);

    const endpoint = format === 'pdf' ? 'timetables/export-pdf' : 'timetables/export';
    const ext = format === 'pdf' ? '.pdf' : '.xlsx';
    const mimeType = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

    try {
      const res = await axios.get(`${API}/${endpoint}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Timetable_${viewParam}${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`${format === 'pdf' ? 'PDF' : 'Excel'} exported successfully`);
    } catch {
      toast.error('Export failed');
    }
  };

  const canExport = () => {
    if (viewMode === 'teacher-grid') return !!selectedTeacher?.trim();
    if (viewMode === 'class-grid') return !!selectedClass?.trim();
    if (viewMode === 'department') return !!selectedSubject?.trim();
    return true;
  };

  // Build calendar grid lookup
  const calendarLookup = {};
  filteredTimetables.forEach(e => {
    const key = `${e.day}-${e.period}`;
    if (!calendarLookup[key]) calendarLookup[key] = [];
    calendarLookup[key].push(e);
  });

  // Build teacher grid lookup: for a specific teacher, {day-period} -> entry
  const teacherGridLookup = useMemo(() => {
    const lookup = {};
    const tid = selectedTeacher?.trim();
    if (!tid) return lookup;
    timetables.filter(e => e.teacher_id === tid).forEach(e => {
      lookup[`${e.day}-${e.period}`] = e;
    });
    return lookup;
  }, [timetables, selectedTeacher]);

  const selectedTeacherName = useMemo(() => {
    const tid = selectedTeacher?.trim();
    if (!tid) return '';
    const t = teachers.find(t => t.id === tid);
    return t ? t.name : '';
  }, [teachers, selectedTeacher]);

  // Count lessons per week for selected teacher
  const teacherWeeklyStats = useMemo(() => {
    const tid = selectedTeacher?.trim();
    if (!tid) return { lessons: 0, free: 0, subjects: [] };
    const entries = timetables.filter(e => e.teacher_id === tid);
    const subjects = [...new Set(entries.map(e => e.subject))];
    return { lessons: entries.length, free: 40 - entries.length, subjects };
  }, [timetables, selectedTeacher]);

  // Class grid lookup: for a specific class, {day-period} -> entry
  const classGridLookup = useMemo(() => {
    const lookup = {};
    const cls = selectedClass?.trim();
    if (!cls) return lookup;
    timetables.filter(e => e.class_name === cls).forEach(e => {
      lookup[`${e.day}-${e.period}`] = e;
    });
    return lookup;
  }, [timetables, selectedClass]);

  const classWeeklyStats = useMemo(() => {
    const cls = selectedClass?.trim();
    if (!cls) return { lessons: 0, free: 0, subjects: [], teachers: [] };
    const entries = timetables.filter(e => e.class_name === cls);
    return {
      lessons: entries.length,
      free: 40 - entries.length,
      subjects: [...new Set(entries.map(e => e.subject))],
      teachers: [...new Set(entries.map(e => e.teacher_name))]
    };
  }, [timetables, selectedClass]);

  // Department view: group teachers by selected subject
  const departmentData = useMemo(() => {
    const subj = selectedSubject?.trim();
    if (!subj) return [];
    const entries = timetables.filter(e => e.subject === subj);
    const byTeacher = {};
    entries.forEach(e => {
      if (!byTeacher[e.teacher_id]) byTeacher[e.teacher_id] = { name: e.teacher_name, id: e.teacher_id, entries: [] };
      byTeacher[e.teacher_id].entries.push(e);
    });
    return Object.values(byTeacher).sort((a, b) => a.name.localeCompare(b.name));
  }, [timetables, selectedSubject]);

  // School heatmap: count busy teachers per day-period
  const heatmapData = useMemo(() => {
    const totalTeachers = teachers.length;
    const map = {};
    timetables.forEach(e => {
      const key = `${e.day}-${e.period}`;
      if (!map[key]) map[key] = new Set();
      map[key].add(e.teacher_id);
    });
    const result = {};
    days.forEach(day => {
      periods.forEach(period => {
        const key = `${day}-${period}`;
        const busy = map[key]?.size || 0;
        result[key] = { busy, free: totalTeachers - busy, pct: totalTeachers > 0 ? Math.round((busy / totalTeachers) * 100) : 0 };
      });
    });
    return result;
  }, [timetables, teachers, days, periods]);

  // Paginated list
  const paginatedList = useMemo(() => {
    const start = (listPage - 1) * LIST_PAGE_SIZE;
    return filteredTimetables.slice(start, start + LIST_PAGE_SIZE);
  }, [filteredTimetables, listPage]);
  const totalPages = Math.max(1, Math.ceil(filteredTimetables.length / LIST_PAGE_SIZE));

  // Subject colors come from shared helper. `getSubjectColor` is imported above.

  return (
    <div data-testid="timetables-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900">Timetables</h1>
          <p className="text-base text-slate-500 mt-2">Manage teacher timetables and import schedules from ASC/STA</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              className="border-slate-300"
              onClick={() => handleExportTimetable('excel')}
              disabled={!canExport()}
              data-testid="export-timetable-button"
            >
              <FileDown className="mr-2 h-4 w-4" />
              Export Excel
            </Button>
            <Button
              variant="outline"
              className="border-slate-300"
              onClick={() => handleExportTimetable('pdf')}
              disabled={!canExport()}
              data-testid="export-pdf-button"
            >
              <FileDown className="mr-2 h-4 w-4" />
              Export PDF
            </Button>
            <Button
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={handleSeedDemo}
              disabled={seedingDemo}
              data-testid="seed-demo-timetable-button"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              {seedingDemo ? 'Seeding...' : 'Seed Demo Timetable'}
            </Button>
            <Button
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => setDeleteStep(1)}
              data-testid="delete-all-timetables-button"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete All
            </Button>

            {/* Import Dialog */}
            <Dialog open={importDialogOpen} onOpenChange={(open) => {
              setImportDialogOpen(open);
              if (!open) resetImport();
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-slate-300" data-testid="import-timetable-button">
                  <Upload className="mr-2 h-4 w-4" />
                  Import CSV/Excel
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Import Timetable</DialogTitle>
                </DialogHeader>
                <div className="space-y-5 mt-2">
                  <div className="rounded-md border border-slate-200 p-4">
                    <div className="flex items-start gap-3">
                      <FileSpreadsheet className="h-5 w-5 text-blue-900 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">Step 1: Download Template</p>
                        <p className="text-xs text-slate-500 mt-1">Download the CSV template, fill it with your timetable data exported from ASC or STA, then upload below.</p>
                        <Button variant="outline" size="sm" className="mt-3" onClick={handleDownloadTemplate} data-testid="download-template-button">
                          <Download className="mr-2 h-3.5 w-3.5" />Download Template
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-md border border-slate-200 p-4">
                    <p className="text-sm font-medium text-slate-900 mb-3">Step 2: Upload File</p>
                    <div className="border-2 border-dashed border-slate-200 rounded-md p-6 text-center cursor-pointer hover:border-blue-300 hover:bg-slate-50 transition-colors" onClick={() => fileInputRef.current?.click()} data-testid="file-drop-zone">
                      <Upload className="mx-auto h-8 w-8 text-slate-400 mb-2" />
                      {importFile ? (
                        <p className="text-sm text-slate-700 font-medium">{importFile.name}</p>
                      ) : (
                        <><p className="text-sm text-slate-600">Click to select a CSV or Excel file</p><p className="text-xs text-slate-400 mt-1">Supports .csv, .xlsx formats</p></>
                      )}
                    </div>
                    <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileChange} className="hidden" data-testid="file-input" />
                  </div>
                  <div className="flex items-center justify-between rounded-md border border-slate-200 p-4">
                    <div>
                      <p className="text-sm font-medium text-slate-900">Replace existing timetable</p>
                      <p className="text-xs text-slate-500 mt-0.5">Clear all current entries before importing</p>
                    </div>
                    <Switch checked={clearExisting} onCheckedChange={setClearExisting} data-testid="clear-existing-switch" />
                  </div>
                  {importResult && (
                    <div className="rounded-md border border-slate-200 p-4 space-y-3" data-testid="import-results">
                      <div className="flex items-center gap-2">
                        {importResult.error_count > 0 ? <AlertCircle className="h-5 w-5 text-amber-500" /> : <CheckCircle2 className="h-5 w-5 text-green-600" />}
                        <p className="text-sm font-medium text-slate-900">{importResult.message}</p>
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-center">
                        <div className="bg-green-50 rounded-md p-2"><p className="text-lg font-bold text-green-700">{importResult.created}</p><p className="text-xs text-green-600">Created</p></div>
                        <div className="bg-slate-50 rounded-md p-2"><p className="text-lg font-bold text-slate-600">{importResult.skipped}</p><p className="text-xs text-slate-500">Skipped</p></div>
                        <div className="bg-red-50 rounded-md p-2"><p className="text-lg font-bold text-red-600">{importResult.error_count}</p><p className="text-xs text-red-500">Errors</p></div>
                      </div>
                      {importResult.errors?.length > 0 && (
                        <div className="max-h-40 overflow-y-auto">
                          <p className="text-xs font-medium text-slate-700 mb-1">Error details:</p>
                          {importResult.errors.map((err, i) => <p key={i} className="text-xs text-red-600 py-0.5">Row {err.row}: {err.error}</p>)}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex justify-end gap-3 pt-2">
                    <Button variant="outline" onClick={() => { setImportDialogOpen(false); resetImport(); }}>Cancel</Button>
                    <Button className="bg-blue-900 hover:bg-blue-800" onClick={handleImport} disabled={!importFile || importing} data-testid="import-submit-button">{importing ? 'Importing...' : 'Import Timetable'}</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* ASC/STA Integration Dialog */}
            <Dialog open={ascDialogOpen} onOpenChange={setAscDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-slate-300" data-testid="asc-integration-button">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  ASC / STA Integration
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>ASC / STA Timetable Integration</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="flex gap-2">
                    <Button variant={ascMode === 'xml' ? 'default' : 'outline'} onClick={() => setAscMode('xml')} className={ascMode === 'xml' ? 'bg-blue-900' : ''} size="sm" data-testid="asc-xml-tab">
                      <FileCode className="mr-1.5 h-4 w-4" />XML Import
                    </Button>
                    <Button variant={ascMode === 'edupage' ? 'default' : 'outline'} onClick={() => setAscMode('edupage')} className={ascMode === 'edupage' ? 'bg-blue-900' : ''} size="sm" data-testid="asc-edupage-tab">
                      <RefreshCw className="mr-1.5 h-4 w-4" />EduPage API Sync
                    </Button>
                  </div>

                  {ascMode === 'xml' ? (
                    <div className="space-y-3">
                      <p className="text-sm text-slate-600">Upload an XML file exported from aSc Timetables desktop software.</p>
                      <p className="text-xs text-slate-400">In aSc Timetables: File → Export → XML Export</p>
                      <label className="cursor-pointer block">
                        <div className="flex items-center justify-center px-4 py-6 border-2 border-dashed border-slate-300 rounded-md hover:border-blue-400 transition-colors">
                          <FileCode className="mr-2 h-5 w-5 text-slate-500" />
                          <span className="text-sm text-slate-600">{syncing ? 'Importing...' : 'Click to upload .xml file'}</span>
                        </div>
                        <input ref={xmlInputRef} type="file" accept=".xml" onChange={handleXmlUpload} className="hidden" data-testid="xml-file-input" />
                      </label>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-slate-600">Sync directly from your school's EduPage account. Requires EduPage login credentials.</p>
                      <div>
                        <Label className="text-xs text-slate-500">EduPage Domain</Label>
                        <Input value={edupageCreds.domain} onChange={(e) => setEdupageCreds({...edupageCreds, domain: e.target.value})} placeholder="yourschool.edupage.org" className="mt-1" data-testid="edupage-domain" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Username / Email</Label>
                        <Input value={edupageCreds.username} onChange={(e) => setEdupageCreds({...edupageCreds, username: e.target.value})} placeholder="admin@school.edu" className="mt-1" data-testid="edupage-username" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Password</Label>
                        <Input type="password" value={edupageCreds.password} onChange={(e) => setEdupageCreds({...edupageCreds, password: e.target.value})} placeholder="Your EduPage password" className="mt-1" data-testid="edupage-password" />
                      </div>
                      <Button onClick={handleEdupageSync} disabled={syncing} className="w-full bg-blue-900 hover:bg-blue-800" data-testid="edupage-sync-button">
                        <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                        {syncing ? 'Syncing...' : 'Sync from EduPage'}
                      </Button>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Add Entry Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-900 hover:bg-blue-800" data-testid="add-timetable-button"><Plus className="mr-2 h-4 w-4" />Add Entry</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Add Timetable Entry</DialogTitle></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div><Label>Teacher</Label><Select value={formData.teacher_id} onValueChange={(value) => setFormData({ ...formData, teacher_id: value })}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Select teacher" /></SelectTrigger><SelectContent>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Day</Label><Select value={formData.day} onValueChange={(value) => setFormData({ ...formData, day: value })}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{days.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Period</Label><Select value={formData.period} onValueChange={(value) => setFormData({ ...formData, period: value })}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Select period" /></SelectTrigger><SelectContent>{periods.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label htmlFor="time-slot">Time Slot</Label><Input id="time-slot" value={formData.time_slot} onChange={(e) => setFormData({ ...formData, time_slot: e.target.value })} placeholder="08:00-09:00" required className="mt-1.5" /></div>
                  <div><Label htmlFor="class">Class</Label><Input id="class" value={formData.class_name} onChange={(e) => setFormData({ ...formData, class_name: e.target.value })} placeholder="3A" required className="mt-1.5" /></div>
                  <div><Label htmlFor="subject">Subject</Label><Input id="subject" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} placeholder="Mathematics" required className="mt-1.5" /></div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-blue-900 hover:bg-blue-800">Create Entry</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-3 items-center">
        <Select value={selectedTeacher} onValueChange={(v) => { setSelectedTeacher(v); setListPage(1); }}>
          <SelectTrigger className="w-52" data-testid="filter-teacher-select"><SelectValue placeholder="Filter by teacher" /></SelectTrigger>
          <SelectContent>
            <SelectItem value=" ">All Teachers</SelectItem>
            {teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={selectedDay} onValueChange={(v) => { setSelectedDay(v); setListPage(1); }}>
          <SelectTrigger className="w-40" data-testid="filter-day-select"><SelectValue placeholder="Filter by day" /></SelectTrigger>
          <SelectContent>
            <SelectItem value=" ">All Days</SelectItem>
            {days.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        {classLevels.length > 0 && (
          <Select value={selectedLevel} onValueChange={(v) => { setSelectedLevel(v === ' ' ? '' : v); setListPage(1); }}>
            <SelectTrigger className="w-44" data-testid="filter-level-select"><SelectValue placeholder="Filter by level" /></SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">All Levels</SelectItem>
              {classLevels.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {(viewMode === 'list' && user?.role === 'admin') && (
          <Select value={selectedPeriod} onValueChange={(v) => { setSelectedPeriod(v); setListPage(1); }}>
            <SelectTrigger className="w-40" data-testid="filter-period-select"><SelectValue placeholder="Filter by period" /></SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">All Periods</SelectItem>
              {periods.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {(viewMode === 'class-grid') && (
          <Select value={selectedClass} onValueChange={setSelectedClass}>
            <SelectTrigger className="w-40" data-testid="filter-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">Select class</SelectItem>
              {allClasses.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {(viewMode === 'department') && (
          <Select value={selectedSubject} onValueChange={setSelectedSubject}>
            <SelectTrigger className="w-48" data-testid="filter-subject-select"><SelectValue placeholder="Select subject" /></SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">Select subject</SelectItem>
              {allSubjects.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {(selectedTeacher?.trim() || selectedDay?.trim() || selectedPeriod?.trim() || selectedClass?.trim() || selectedSubject?.trim() || searchQuery) && (
          <Button variant="ghost" size="sm"
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={() => { setSelectedTeacher(''); setSelectedDay(''); setSelectedPeriod(''); setSelectedClass(''); setSelectedSubject(''); setSearchQuery(''); setListPage(1); setViewMode('list'); }}
            data-testid="clear-all-filters-btn">
            <X className="h-3.5 w-3.5 mr-1" /> Clear All Filters
          </Button>
        )}
      </div>
      <div className="mb-4 flex flex-wrap gap-1">
        {user?.role !== 'admin' && (
          <Button
            variant={(viewMode === 'teacher-grid' && selectedTeacher === user?.id) ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setViewMode('teacher-grid'); setSelectedTeacher(user?.id || ''); }}
            className={(viewMode === 'teacher-grid' && selectedTeacher === user?.id) ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}
            data-testid="my-timetable-btn"
          >
            <CalendarIcon className="h-4 w-4 mr-1" /> My Timetable
          </Button>
        )}
        {[
          { key: 'teacher-grid', icon: CalendarIcon, label: 'Teacher' },
          { key: 'class-grid', icon: BookOpen, label: 'Class' },
          { key: 'department', icon: Users, label: 'Dept' },
          { key: 'heatmap', icon: BarChart3, label: 'Heatmap' },
          { key: 'calendar', icon: Grid3x3, label: 'Calendar' },
          { key: 'list', icon: ClipboardList, label: 'List' },
        ].map(v => (
          <Button key={v.key} variant={viewMode === v.key ? 'default' : 'outline'} size="sm"
            onClick={() => setViewMode(v.key)}
            className={viewMode === v.key ? 'bg-blue-900 hover:bg-blue-800' : ''}
            data-testid={`view-${v.key}-btn`}>
            <v.icon className="h-4 w-4 mr-1" /> {v.label}
          </Button>
        ))}
      </div>

      <div className="relative mb-4" data-testid="timetable-search">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
          placeholder="Quick search — type a teacher, class, or subject to jump..."
          className="pl-9"
          data-testid="timetable-search-input"
        />
        {searchFocused && searchResults && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto" data-testid="search-results-dropdown">
            {searchResults.teachers.length > 0 && (
              <div>
                <p className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider bg-slate-50">Teachers</p>
                {searchResults.teachers.map(t => (
                  <button key={t.id} onMouseDown={() => handleSearchSelect('teacher', t.id)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center gap-2 transition-colors"
                    data-testid={`search-result-teacher-${t.id}`}>
                    <CalendarIcon className="h-3.5 w-3.5 text-blue-500" />
                    <span className="text-slate-800">{t.name}</span>
                    <span className="ml-auto text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">Teacher Grid</span>
                  </button>
                ))}
              </div>
            )}
            {searchResults.classes.length > 0 && (
              <div>
                <p className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider bg-slate-50">Classes</p>
                {searchResults.classes.map(c => (
                  <button key={c} onMouseDown={() => handleSearchSelect('class', c)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center gap-2 transition-colors"
                    data-testid={`search-result-class-${c}`}>
                    <BookOpen className="h-3.5 w-3.5 text-emerald-500" />
                    <span className="text-slate-800">{c}</span>
                    <span className="ml-auto text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">Class Grid</span>
                  </button>
                ))}
              </div>
            )}
            {searchResults.subjects.length > 0 && (
              <div>
                <p className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider bg-slate-50">Subjects</p>
                {searchResults.subjects.map(s => (
                  <button key={s} onMouseDown={() => handleSearchSelect('subject', s)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center gap-2 transition-colors"
                    data-testid={`search-result-subject-${s}`}>
                    <Users className="h-3.5 w-3.5 text-violet-500" />
                    <span className="text-slate-800">{s}</span>
                    <span className="ml-auto text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">Department</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-slate-500">Loading timetables...</div>
      ) : timetables.length === 0 ? (
        <Card className="p-8 text-center">
          <CalendarIcon className="mx-auto h-12 w-12 text-slate-300 mb-4" />
          <p className="text-slate-500 mb-2">No timetable entries found.</p>
          {isAdmin && <p className="text-sm text-slate-400">Use "Import CSV/Excel" to bulk-upload timetable data from ASC or STA exports.</p>}
        </Card>
      ) : viewMode === 'calendar' ? (
        <CalendarGridView days={days} periods={periods} calendarLookup={calendarLookup} />
      ) : viewMode === 'teacher-grid' ? (
        <TeacherGridView
          days={days}
          periods={periods}
          selectedTeacher={selectedTeacher}
          selectedTeacherName={selectedTeacherName}
          teacherWeeklyStats={teacherWeeklyStats}
          teacherGridLookup={teacherGridLookup}
        />
      ) : viewMode === 'class-grid' ? (
        <ClassGridView
          days={days}
          periods={periods}
          selectedClass={selectedClass}
          classWeeklyStats={classWeeklyStats}
          classGridLookup={classGridLookup}
        />
      ) : viewMode === 'department' ? (
        <DepartmentGridView
          days={days}
          periods={periods}
          selectedSubject={selectedSubject}
          departmentData={departmentData}
        />
      ) : viewMode === 'heatmap' ? (
        <HeatmapView days={days} periods={periods} heatmapData={heatmapData} teachersCount={teachers.length} />
      ) : (
        /* Paginated List View (Day Tabs) */
        <Tabs defaultValue={selectedDay?.trim() || days.find(d => groupedByDay[d]?.length > 0) || 'Monday'} className="w-full">
          <TabsList className="mb-4 flex-wrap h-auto gap-1">
            {days.map(day => (
              <TabsTrigger key={day} value={day} className="text-sm" data-testid={`tab-${day.toLowerCase()}`} disabled={selectedDay && selectedDay.trim() && selectedDay !== day}>
                {day}
                {groupedByDay[day]?.length > 0 && <span className="ml-1.5 text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">{groupedByDay[day].length}</span>}
              </TabsTrigger>
            ))}
          </TabsList>

          {days.map(day => {
            const dayEntries = groupedByDay[day] || [];
            const dayPageEntries = dayEntries.slice((listPage - 1) * LIST_PAGE_SIZE, listPage * LIST_PAGE_SIZE);
            const dayTotalPages = Math.max(1, Math.ceil(dayEntries.length / LIST_PAGE_SIZE));
            return (
            <TabsContent key={day} value={day}>
              {dayEntries.length === 0 ? (
                <div className="text-center py-8 text-sm text-slate-400">No entries for {day}</div>
              ) : (
                <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid={`timetable-table-${day.toLowerCase()}`}>
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left p-3 font-medium text-slate-500">Period</th>
                        <th className="text-left p-3 font-medium text-slate-500">Time</th>
                        <th className="text-left p-3 font-medium text-slate-500">Teacher</th>
                        <th className="text-left p-3 font-medium text-slate-500">Class</th>
                        <th className="text-left p-3 font-medium text-slate-500">Subject</th>
                        {isAdmin && <th className="text-right p-3 font-medium text-slate-500">Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {dayPageEntries.map((entry) => (
                        <tr key={entry.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                          <td className="p-3 font-medium text-slate-900">{entry.period}</td>
                          <td className="p-3 text-slate-600">{entry.time_slot}</td>
                          <td className="p-3 text-slate-700">{entry.teacher_name}</td>
                          <td className="p-3 text-slate-700">{entry.class_name}</td>
                          <td className="p-3 text-slate-700">{entry.subject}</td>
                          {isAdmin && (
                            <td className="p-3 text-right">
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-entry-${entry.id}`}>Delete</Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {dayTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-200">
                    <p className="text-xs text-slate-500">Showing {Math.min((listPage - 1) * LIST_PAGE_SIZE + 1, dayEntries.length)}-{Math.min(listPage * LIST_PAGE_SIZE, dayEntries.length)} of {dayEntries.length} entries</p>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" disabled={listPage <= 1} onClick={() => setListPage(p => p - 1)} data-testid="list-prev-page">
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm text-slate-600 font-medium">Page {listPage} / {dayTotalPages}</span>
                      <Button variant="outline" size="sm" disabled={listPage >= dayTotalPages} onClick={() => setListPage(p => p + 1)} data-testid="list-next-page">
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
                </>
              )}
            </TabsContent>
            );
          })}
        </Tabs>
      )}

      {/* Common Free Periods Finder */}
      <Card className="dashboard-card p-4 sm:p-6 mt-6" data-testid="common-free-periods-card">
        <div className="flex items-center gap-2 mb-1">
          <Users className="h-5 w-5 text-blue-900" strokeWidth={1.5} />
          <h2 className="text-lg font-semibold text-slate-900">Common Free Periods Finder</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">Select teachers and a day to find overlapping free periods for ad-hoc meetings or coordination.</p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          {/* Teacher Selection */}
          <div className="lg:col-span-2">
            <Label className="text-xs text-slate-500 mb-1.5 block">Select Teachers (min 2)</Label>
            <Input
              placeholder="Search teachers..."
              value={cfpTeacherSearch}
              onChange={(e) => setCfpTeacherSearch(e.target.value)}
              className="mb-2"
              data-testid="cfp-teacher-search"
            />
            <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-md p-1.5 space-y-0.5" data-testid="cfp-teacher-list">
              {teachers
                .filter(t => !cfpTeacherSearch || t.name.toLowerCase().includes(cfpTeacherSearch.toLowerCase()))
                .map(t => {
                  const selected = cfpSelectedTeachers.includes(t.id);
                  return (
                    <label key={t.id}
                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded cursor-pointer transition-colors text-sm ${
                        selected ? 'bg-blue-50 text-blue-900 font-medium' : 'hover:bg-slate-50 text-slate-700'
                      }`}>
                      <input type="checkbox" checked={selected} onChange={() => toggleCfpTeacher(t.id)}
                        className="rounded border-slate-300" data-testid={`cfp-teacher-${t.id}`} />
                      {t.name}
                    </label>
                  );
                })}
            </div>
            {cfpSelectedTeachers.length > 0 && (
              <p className="text-xs text-slate-500 mt-1">{cfpSelectedTeachers.length} teacher{cfpSelectedTeachers.length > 1 ? 's' : ''} selected</p>
            )}
          </div>

          {/* Day + Find button */}
          <div>
            <Label className="text-xs text-slate-500 mb-1.5 block">Day</Label>
            <Select value={cfpDay} onValueChange={(v) => { setCfpDay(v); setCfpResult(null); }}>
              <SelectTrigger data-testid="cfp-day-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {days.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={findCommonFreePeriods} disabled={cfpLoading || cfpSelectedTeachers.length < 2}
              className="w-full mt-3 bg-blue-900 hover:bg-blue-800" data-testid="cfp-find-button">
              <Clock className="mr-2 h-4 w-4" />
              {cfpLoading ? 'Finding...' : 'Find Common Free Periods'}
            </Button>
            {cfpSelectedTeachers.length > 0 && cfpSelectedTeachers.length < 2 && (
              <p className="text-xs text-amber-600 mt-1.5">Select at least 1 more teacher</p>
            )}
          </div>
        </div>

        {/* Results Grid */}
        {cfpResult && (
          <div className="mt-4" data-testid="cfp-results">
            {/* Common Free Summary */}
            <div className={`p-3 rounded-lg mb-4 ${cfpResult.common_free_periods.length > 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 border border-slate-200'}`}>
              {cfpResult.common_free_periods.length > 0 ? (
                <div>
                  <p className="text-sm font-semibold text-emerald-800 mb-1" data-testid="cfp-result-count">
                    {cfpResult.common_free_periods.length} common free period{cfpResult.common_free_periods.length > 1 ? 's' : ''} found on {cfpDay}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {cfpResult.common_free_periods.map(p => (
                      <span key={p} className="px-3 py-1 bg-emerald-600 text-white text-xs font-semibold rounded-full" data-testid="cfp-common-period">{p}</span>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-600" data-testid="cfp-no-common">No common free periods on {cfpDay}. All selected teachers have conflicting schedules.</p>
              )}
            </div>

            {/* Per-Teacher Visual Grid */}
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm border-collapse" data-testid="cfp-grid">
                <thead>
                  <tr>
                    <th className="p-2 text-left font-medium text-slate-500 border-b border-slate-200 min-w-[120px]">Teacher</th>
                    {(cfpResult.all_periods || []).map(p => (
                      <th key={p} className="p-2 text-center font-medium text-slate-500 border-b border-slate-200 min-w-[80px]">{p.replace('Period ', 'P')}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(cfpResult.teacher_details || {}).map(([tid, info]) => (
                    <tr key={tid} className="border-b border-slate-100">
                      <td className="p-2 font-medium text-slate-800 whitespace-nowrap">{info.name}</td>
                      {(cfpResult.all_periods || []).map(p => {
                        const isCommon = cfpResult.common_free_periods.includes(p);
                        const isFree = info.free_periods.includes(p);
                        const busy = info.busy_periods[p];
                        return (
                          <td key={p} className={`p-1.5 text-center border-l border-slate-100 ${
                            isCommon && isFree ? 'bg-emerald-100' :
                            isFree ? 'bg-blue-50' :
                            'bg-slate-50'
                          }`} data-testid={`cfp-cell-${tid}-${p.replace(' ', '-').toLowerCase()}`}>
                            {busy ? (
                              <div className="px-1 py-0.5 rounded text-[10px]">
                                <p className="font-semibold text-slate-700">{busy.class_name}</p>
                                <p className="text-slate-500">{busy.subject}</p>
                              </div>
                            ) : (
                              <span className={`text-xs font-medium ${isCommon ? 'text-emerald-700' : 'text-blue-600'}`}>
                                {isCommon ? 'Common Free' : 'Free'}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300"></span> Common Free Period</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-blue-50 border border-blue-200"></span> Individual Free</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-slate-50 border border-slate-200"></span> Busy (Teaching)</span>
            </div>
          </div>
        )}
      </Card>

      {/* Delete All — Step 1: Warning */}
      <AlertDialog open={deleteStep === 1} onOpenChange={(open) => { if (!open) setDeleteStep(0); }}>
        <AlertDialogContent data-testid="delete-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              Delete All Timetable Entries?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-600">
              This will <strong>permanently delete ALL {timetables.length} timetable entries</strong> across all teachers, classes, and days. This action cannot be undone.
              <br /><br />
              Are you sure you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteStep(0)} data-testid="delete-cancel-step1">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setDeleteStep(2); setDeleteConfirmText(''); }} className="bg-red-600 hover:bg-red-700" data-testid="delete-proceed-step1">
              Yes, I Want to Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All — Step 2: Type confirmation */}
      <AlertDialog open={deleteStep === 2} onOpenChange={(open) => { if (!open) { setDeleteStep(0); setDeleteConfirmText(''); } }}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" />
              Final Confirmation Required
            </AlertDialogTitle>
            <AlertDialogDescription>
              To confirm deletion of all timetable entries, type DELETE ALL in the box below.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="Type DELETE ALL"
            className="font-mono text-center border-red-200 focus:border-red-400 my-2"
            data-testid="delete-confirm-input"
          />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDeleteStep(0); setDeleteConfirmText(''); }} data-testid="delete-cancel-step2">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAllConfirmed}
              disabled={deleteConfirmText !== 'DELETE ALL'}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="delete-final-confirm"
            >
              Permanently Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TimetablesPage;
